import { useState } from "react";
import { CheckCircle2, Copy, Play, ShieldCheck, Signature } from "lucide-react";
import { SecondaryButton } from "@ocs/ui";
import {
  concatHex,
  createPublicClient,
  createWalletClient,
  custom,
  http,
  parseAbi,
  recoverMessageAddress,
  type Address,
  type EIP1193Provider,
  type Hex
} from "viem";
import { bsc } from "viem/chains";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000" as Address;
const BSC_RPC_URL = (import.meta.env.VITE_BSC_RPC_URL as string | undefined)?.trim()
  || "https://bsc-dataseed.bnbchain.org";
const safePublicClient = createPublicClient({ chain: bsc, transport: http(BSC_RPC_URL) });
const SAFE_ABI = parseAbi([
  "function getOwners() view returns (address[])",
  "function getThreshold() view returns (uint256)",
  "function nonce() view returns (uint256)",
  "function getTransactionHash(address to,uint256 value,bytes data,uint8 operation,uint256 safeTxGas,uint256 baseGas,uint256 gasPrice,address gasToken,address refundReceiver,uint256 _nonce) view returns (bytes32)",
  "function execTransaction(address to,uint256 value,bytes data,uint8 operation,uint256 safeTxGas,uint256 baseGas,uint256 gasPrice,address gasToken,address payable refundReceiver,bytes signatures) payable returns (bool success)"
]);

type StoredSignature = { owner: Address; signature: Hex };
type SafeBundle = {
  version: 1;
  chainId: 56;
  safe: Address;
  to: Address;
  value: "0";
  data: Hex;
  operation: 0;
  safeTxGas: "0";
  baseGas: "0";
  gasPrice: "0";
  gasToken: Address;
  refundReceiver: Address;
  nonce: string;
  safeTxHash: Hex;
  signatures: StoredSignature[];
};

type Props = {
  label: string;
  safe: Address;
  to: Address;
  data: Hex;
  onExecuted?: (txHash: Hex) => void;
};

function provider(): EIP1193Provider {
  const injected = (window as Window & { ethereum?: EIP1193Provider }).ethereum;
  if (!injected) throw new Error("未检测到钱包 Provider，请在 TP Wallet DApp 浏览器中打开管理中台");
  return injected;
}

function safeEthSignSignature(signature: Hex): Hex {
  const rawV = Number.parseInt(signature.slice(-2), 16);
  const canonicalV = rawV < 27 ? rawV + 27 : rawV;
  if (canonicalV !== 27 && canonicalV !== 28) throw new Error("钱包返回了不支持的签名格式");
  return `${signature.slice(0, -2)}${(canonicalV + 4).toString(16).padStart(2, "0")}` as Hex;
}

function personalSignature(signature: Hex): Hex {
  const safeV = Number.parseInt(signature.slice(-2), 16);
  if (safeV !== 31 && safeV !== 32) throw new Error("签名不是 Safe personal_sign 格式");
  return `${signature.slice(0, -2)}${(safeV - 4).toString(16).padStart(2, "0")}` as Hex;
}

function normalize(address: string) {
  return address.toLowerCase();
}

export function SafeMultisigTransaction({ label, safe, to, data, onExecuted }: Props) {
  const [bundle, setBundle] = useState<SafeBundle | null>(null);
  const [exchange, setExchange] = useState("");
  const [account, setAccount] = useState<Address | null>(null);
  const [threshold, setThreshold] = useState(2);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function clients(connect = false) {
    const injected = provider();
    if (connect) {
      const chainId = await injected.request({ method: "eth_chainId" });
      if (String(chainId).toLowerCase() !== "0x38") {
        await injected.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x38" }] });
      }
    }
    const walletClient = createWalletClient({ chain: bsc, transport: custom(injected) });
    const addresses = connect ? await walletClient.requestAddresses() : [];
    const selected = addresses[0] ?? account;
    if (connect && !selected) throw new Error("钱包没有返回连接账户");
    if (selected) setAccount(selected);
    return { publicClient: safePublicClient, walletClient, selected };
  }

  async function validate(candidate: SafeBundle) {
    if (candidate.version !== 1 || candidate.chainId !== 56) throw new Error("签名包网络或版本不正确");
    if (normalize(candidate.safe) !== normalize(safe)) throw new Error("签名包多签地址不正确");
    if (normalize(candidate.to) !== normalize(to) || candidate.data.toLowerCase() !== data.toLowerCase()) {
      throw new Error("签名包交易目标与当前管理申请不一致");
    }
    const { publicClient } = await clients();
    const [owners, currentThreshold, currentNonce, calculatedHash] = await Promise.all([
      publicClient.readContract({ address: safe, abi: SAFE_ABI, functionName: "getOwners" }),
      publicClient.readContract({ address: safe, abi: SAFE_ABI, functionName: "getThreshold" }),
      publicClient.readContract({ address: safe, abi: SAFE_ABI, functionName: "nonce" }),
      publicClient.readContract({
        address: safe,
        abi: SAFE_ABI,
        functionName: "getTransactionHash",
        args: [to, 0n, data, 0, 0n, 0n, 0n, ZERO_ADDRESS, ZERO_ADDRESS, BigInt(candidate.nonce)]
      })
    ]);
    if (BigInt(candidate.nonce) !== currentNonce) throw new Error(`交易 nonce 已过期，链上当前 nonce 为 ${currentNonce}`);
    if (calculatedHash.toLowerCase() !== candidate.safeTxHash.toLowerCase()) throw new Error("签名包哈希校验失败");
    const ownerSet = new Set(owners.map(normalize));
    const seen = new Set<string>();
    for (const item of candidate.signatures) {
      const recovered = await recoverMessageAddress({
        message: { raw: candidate.safeTxHash },
        signature: personalSignature(item.signature)
      });
      if (normalize(recovered) !== normalize(item.owner)) throw new Error("签名恢复地址与 Owner 不一致");
      if (!ownerSet.has(normalize(item.owner))) throw new Error("签名者不是当前多签 Owner");
      if (seen.has(normalize(item.owner))) throw new Error("签名包存在重复 Owner");
      seen.add(normalize(item.owner));
    }
    setThreshold(Number(currentThreshold));
    return { owners, threshold: Number(currentThreshold) };
  }

  async function run(action: string, task: () => Promise<void>) {
    setBusy(action);
    setError("");
    setMessage("");
    try {
      await task();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "操作失败");
    } finally {
      setBusy("");
    }
  }

  function createBundle() {
    return run("create", async () => {
      const { publicClient } = await clients(true);
      const [nonce, owners, currentThreshold] = await Promise.all([
        publicClient.readContract({ address: safe, abi: SAFE_ABI, functionName: "nonce" }),
        publicClient.readContract({ address: safe, abi: SAFE_ABI, functionName: "getOwners" }),
        publicClient.readContract({ address: safe, abi: SAFE_ABI, functionName: "getThreshold" })
      ]);
      if (!account && !owners.length) throw new Error("无法读取多签 Owner");
      const safeTxHash = await publicClient.readContract({
        address: safe,
        abi: SAFE_ABI,
        functionName: "getTransactionHash",
        args: [to, 0n, data, 0, 0n, 0n, 0n, ZERO_ADDRESS, ZERO_ADDRESS, nonce]
      });
      const next: SafeBundle = {
        version: 1,
        chainId: 56,
        safe,
        to,
        value: "0",
        data,
        operation: 0,
        safeTxGas: "0",
        baseGas: "0",
        gasPrice: "0",
        gasToken: ZERO_ADDRESS,
        refundReceiver: ZERO_ADDRESS,
        nonce: nonce.toString(),
        safeTxHash,
        signatures: []
      };
      setThreshold(Number(currentThreshold));
      setBundle(next);
      setExchange(JSON.stringify(next, null, 2));
      setMessage(`签名包已创建，nonce ${nonce}，需要 ${currentThreshold} 个 Owner 签名`);
    });
  }

  function importBundle() {
    return run("import", async () => {
      const parsed = JSON.parse(exchange) as SafeBundle;
      const state = await validate(parsed);
      setBundle(parsed);
      setMessage(`签名包已验证，当前 ${parsed.signatures.length}/${state.threshold} 签名`);
    });
  }

  function signBundle() {
    return run("sign", async () => {
      if (!bundle) throw new Error("请先创建或导入签名包");
      await validate(bundle);
      const { walletClient, selected } = await clients(true);
      if (!selected) throw new Error("请连接 Owner 钱包");
      const owners = await safePublicClient.readContract({
        address: safe, abi: SAFE_ABI, functionName: "getOwners"
      });
      if (!owners.some((owner) => normalize(owner) === normalize(selected))) throw new Error("当前连接账户不是多签 Owner");
      if (bundle.signatures.some((item) => normalize(item.owner) === normalize(selected))) throw new Error("当前 Owner 已经签署过此交易");
      const signed = await walletClient.signMessage({ account: selected, message: { raw: bundle.safeTxHash } });
      const recovered = await recoverMessageAddress({ message: { raw: bundle.safeTxHash }, signature: signed });
      if (normalize(recovered) !== normalize(selected)) throw new Error("钱包签名校验失败");
      const next = {
        ...bundle,
        signatures: [...bundle.signatures, { owner: selected, signature: safeEthSignSignature(signed) }]
      };
      setBundle(next);
      setExchange(JSON.stringify(next, null, 2));
      setMessage(`签名完成：${next.signatures.length}/${threshold}。请复制签名包给下一位 Owner。`);
    });
  }

  function executeBundle() {
    return run("execute", async () => {
      if (!bundle) throw new Error("请先创建或导入签名包");
      const state = await validate(bundle);
      if (bundle.signatures.length < state.threshold) throw new Error(`签名不足，需要 ${state.threshold} 个 Owner 签名`);
      const { publicClient, walletClient, selected } = await clients(true);
      if (!selected) throw new Error("请连接用于支付 Gas 的 Owner 钱包");
      const signatures = concatHex(
        [...bundle.signatures]
          .sort((a, b) => normalize(a.owner).localeCompare(normalize(b.owner)))
          .map((item) => item.signature)
      );
      const txHash = await walletClient.writeContract({
        account: selected,
        address: safe,
        abi: SAFE_ABI,
        functionName: "execTransaction",
        args: [to, 0n, data, 0, 0n, 0n, 0n, ZERO_ADDRESS, ZERO_ADDRESS, signatures]
      });
      const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
      if (receipt.status !== "success") throw new Error(`多签交易执行失败：${txHash}`);
      onExecuted?.(txHash);
      setMessage(`多签交易已链上确认：${txHash}`);
    });
  }

  async function copyBundle() {
    if (!bundle) return;
    await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
    setMessage("签名包已复制，可发送给下一位 Owner 导入");
  }

  return (
    <section className="safe-transaction">
      <header><ShieldCheck size={17} /><strong>{label}</strong><span>{bundle?.signatures.length ?? 0}/{threshold} 签名</span></header>
      <p>仅允许执行当前提现申请生成的固定合约与 calldata，不提供任意地址或金额输入。</p>
      <div className="safe-actions">
        <SecondaryButton disabled={Boolean(busy)} onClick={createBundle}>创建签名包</SecondaryButton>
        <SecondaryButton disabled={!bundle || Boolean(busy)} onClick={signBundle}><Signature size={15} />Owner 签名</SecondaryButton>
        <SecondaryButton disabled={!bundle || Boolean(busy)} onClick={copyBundle}><Copy size={15} />复制签名包</SecondaryButton>
        <SecondaryButton disabled={!bundle || bundle.signatures.length < threshold || Boolean(busy)} onClick={executeBundle}><Play size={15} />执行多签</SecondaryButton>
      </div>
      <textarea
        value={exchange}
        onChange={(event) => setExchange(event.target.value)}
        placeholder="第一位 Owner 签名后复制此签名包；下一位 Owner 在自己的设备粘贴并导入"
        aria-label={`${label} Safe 多签签名包`}
      />
      <SecondaryButton disabled={!exchange || Boolean(busy)} onClick={importBundle}>导入并校验签名包</SecondaryButton>
      {account && <small>当前钱包：{account}</small>}
      {message && <small className="safe-success"><CheckCircle2 size={14} />{message}</small>}
      {error && <small className="cell-error">{error}</small>}
    </section>
  );
}
